import React from 'react';
import { useLocation } from 'react-router-dom';

import { MOBILE_MQ } from '../theme/tokens';

/**
 * Per-session window-scroll memory, keyed by PATHNAME (not the history key), so a
 * page is ONE scroll context: drilling into a different path starts fresh, and
 * coming back restores the page's offset. Its own map (not shared with the
 * inner-container hook).
 */
const positions = new Map<string, number>();

const isMobile = (): boolean => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches;

/**
 * Window-scroll restoration for the nova area on MOBILE, where the
 * layout makes the document itself the scroller so the browser chrome reacts to
 * scrolling (see `MainLayout`'s `MobileBodyScroll`). The companion to
 * `useScrollRestoration`, which owns the inner-container scroll on desktop; each
 * hook is inert on the breakpoint it does not own.
 *
 * It restores per page on mount (a freshly mounted layer is a new page → jump to
 * its saved offset, or the top for a never-seen path) and tracks the scroll
 * thereafter. It does NOT try to fight in-page (same-pathname) scroll resets: on
 * mobile those are avoided at the source by NOT navigating for tab/filter changes
 * (see `Campaigns` — local state, no `setSearchParams`), because iOS Safari snaps
 * the document scroll whenever a navigation makes it re-reveal its toolbar, and no
 * after-the-fact correction can do that cleanly.
 *
 * A layer renders ONE page for its whole life (the frozen exiting layer keeps
 * showing its old page during a slide), so the pinned pathname is how it tells
 * whether it is the page currently on screen — only the on-screen page may restore
 * or persist, so the exiting layer can't clobber the incoming page's offset on the
 * shared window scroller.
 */
export const useWindowScrollRestoration = (layerRef: React.RefObject<HTMLElement>): void => {
  const location = useLocation();
  // This layer's page — pinned on mount, never the live one.
  const pathRef = React.useRef(location.pathname);
  // The live route, refreshed every render, so the listener can tell at scroll
  // time whether THIS layer is still the on-screen page.
  const liveLocRef = React.useRef(location);
  liveLocRef.current = location;
  const isLive = React.useCallback((): boolean => pathRef.current === liveLocRef.current.pathname, []);

  // Restore on mount + track scroll. Layout effect so the restore lands before
  // paint — no flash at the top before jumping down. Runs once per layer instance.
  React.useLayoutEffect(() => {
    if (!isMobile()) return undefined;
    // The frozen exiting layer isn't the on-screen page — don't move or track it.
    if (!isLive()) return undefined;

    const path = pathRef.current;
    window.scrollTo(0, positions.get(path) ?? 0);

    const onScroll = (): void => {
      // Persist only while this layer is the on-screen page, so a leaving layer
      // can't overwrite the incoming page's restored offset on the shared scroller.
      if (isLive()) positions.set(path, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [layerRef, isLive]);
};

export default useWindowScrollRestoration;
